import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Alert, Box, Button, Card, CardContent, Checkbox, Chip, CircularProgress,
  Divider, FormControlLabel, Grid, Stack, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, TextField, Typography,
} from "@mui/material";
import {
  applyTrainerDeferredAllocation, fetchAwaitingTrainerAllocationQueue,
  fetchTrainerDeferredAllocationHistory, previewTrainerDeferredAllocation,
} from "./trainerInventoryApi";
import {
  allocationDraftError, makeDeferredAllocationId, makeDeferredAllocationPayload,
  mergeAllocationGroups, parseAllocationCount, persistPendingTrainerAllocation,
  readPendingTrainerAllocation, requestedPhysicalCards,
} from "./quickTrainerAllocationUtils";

const formatQuantity = (value) => Number(value || 0).toLocaleString("en-AU");
const errorMessage = (error, fallback) => error?.response?.data?.error || fallback;
const cachedQuantity = (quantity, status, syncedAt) =>
  `${quantity === null || quantity === undefined ? "Unknown" : formatQuantity(quantity)} · ${status || "Unknown status"} · ${syncedAt ? new Date(syncedAt).toLocaleString("en-AU") : "No sync date"}`;

export default function QuickTrainerAllocation({ onOpenInventory, onAllocationApplied }) {
  const [groups, setGroups] = useState([]);
  const [afterId, setAfterId] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [chosen, setChosen] = useState(null);
  const [singles, setSingles] = useState("0");
  const [playsets, setPlaysets] = useState("0");
  const [preview, setPreview] = useState(null);
  const [confirmed, setConfirmed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [pending, setPending] = useState(readPendingTrainerAllocation);
  const [history, setHistory] = useState(null);
  const [historyBusy, setHistoryBusy] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const loadingRef = useRef(false);
  const sendingRef = useRef(false);

  const loadQueue = useCallback(async (nextAfterId = 0) => {
    if (loadingRef.current) return;
    loadingRef.current = true;
    setLoading(true);
    setError("");
    try {
      const response = await fetchAwaitingTrainerAllocationQueue(nextAfterId);
      const data = response.data || {};
      setGroups((current) => nextAfterId ? mergeAllocationGroups(current, data.groups || []) : data.groups || []);
      setAfterId(data.next_after_id || nextAfterId);
      setHasMore(Boolean(data.has_more));
    } catch (requestError) {
      setError(errorMessage(requestError, "Cannot load Awaiting Allocation. No quantities were changed."));
    } finally {
      loadingRef.current = false;
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadQueue(); }, [loadQueue]);

  const selectReceipt = (group, source) => {
    if (pending || busy || source.blocked_reason) return;
    setChosen({ group, source });
    setSingles("0"); setPlaysets("0");
    setPreview(null); setConfirmed(false); setHistory(null);
    setError(""); setMessage("");
  };

  const changeQuantity = (role, value) => {
    if (pending || busy) return;
    if (role === "single") setSingles(value);
    else setPlaysets(value);
    setPreview(null); setConfirmed(false); setError("");
  };

  const draftError = chosen ? allocationDraftError(singles, playsets, chosen.source) : "Select an intake receipt.";
  const requested = requestedPhysicalCards(parseAllocationCount(singles) || 0, parseAllocationCount(playsets) || 0);

  const reviewPreview = async () => {
    if (!chosen || draftError || busy || pending) return;
    setBusy(true); setError(""); setMessage(""); setPreview(null); setConfirmed(false);
    const payload = makeDeferredAllocationPayload(chosen.group, Number(singles), Number(playsets), makeDeferredAllocationId());
    try {
      const response = await previewTrainerDeferredAllocation(chosen.source.intake_row_id, payload);
      if (response.data?.already_applied) {
        setMessage("This allocation request was previously applied. Reload the queue before deciding on further stock.");
        await loadQueue();
        setChosen(null);
      } else if (response.data?.projection) {
        setPreview({ payload, projection: response.data.projection, rowId: chosen.source.intake_row_id });
      } else {
        setError("Preview was incomplete. Nothing has been allocated.");
      }
    } catch (requestError) {
      setError(errorMessage(requestError, "Preview failed. Refresh stock and try again."));
    } finally { setBusy(false); }
  };

  const sendAllocation = async (operation) => {
    if (sendingRef.current) return;
    sendingRef.current = true;
    setBusy(true); setError(""); setMessage("");
    try {
      const response = await applyTrainerDeferredAllocation(operation.rowId, operation.payload);
      // Both newly committed and already-applied are authoritative successes.
      if (response.data?.success !== true) throw new Error("Allocation response did not confirm success.");
      const cleared = persistPendingTrainerAllocation(null);
      if (!cleared) {
        setError("Allocation succeeded, but the saved retry token could not be cleared. Check browser storage before proceeding.");
        return;
      }
      setPending(null);
      setChosen(null); setPreview(null); setHistory(null); setConfirmed(false);
      setMessage(response.data?.already_applied
        ? "Already applied: server confirmed the original allocation. Nothing was allocated twice."
        : "Allocation applied. Local singles/playsets targets changed; eBay was not updated.");
      await loadQueue();
      onAllocationApplied?.();
    } catch (requestError) {
      // Network/5xx responses may arrive after the DB transaction committed.
      // Hold the exact request for safe idempotent retry across refreshes.
      if (requestError?.response?.status >= 400 && requestError.response.status < 500) {
        persistPendingTrainerAllocation(null);
        setPending(null);
        setPreview(null); setConfirmed(false);
        setError(`${errorMessage(requestError, "Allocation rejected.")} No allocation was committed by this rejected request. Refresh the queue and preview again.`);
        await loadQueue();
      } else {
        setError(`${errorMessage(requestError, requestError.message || "Response uncertain.")} The server may have allocated stock. Check history or retry this exact operation ID; do not submit a new allocation.`);
      }
    } finally { sendingRef.current = false; setBusy(false); }
  };

  const confirmAllocation = () => {
    if (!preview || !confirmed || pending || busy || draftError || !chosen || preview.rowId !== chosen.source.intake_row_id) return;
    const operation = { rowId: preview.rowId, payload: preview.payload };
    // A missing persistent recovery slot is a hard stop, not a best-effort save.
    if (!persistPendingTrainerAllocation(operation)) {
      setError("Browser storage is unavailable. Cannot safely recover from a timed-out allocation; enable storage before applying.");
      return;
    }
    setPending(operation);
    sendAllocation(operation);
  };

  const verifyPending = async () => {
    if (!pending || busy) return;
    setBusy(true); setError("");
    try {
      const response = await fetchTrainerDeferredAllocationHistory(pending.rowId);
      const event = (response.data?.events || []).find((item) => item.request_id === pending.payload.request_id);
      if (event) {
        if (persistPendingTrainerAllocation(null)) {
          setPending(null); setChosen(null); setPreview(null); setConfirmed(false);
          setMessage(`Allocation event #${event.id} is recorded. The saved retry operation has been cleared.`);
          await loadQueue();
        } else setError("Allocation is confirmed, but browser storage could not be cleared. Please resolve storage before continuing.");
      } else {
        setMessage("No matching event is recorded yet. Use Retry same allocation to resolve the pending request safely; do not create a fresh request.");
      }
    } catch (requestError) {
      setError(errorMessage(requestError, "Unable to confirm allocation status. Keep the original retry token."));
    } finally { setBusy(false); }
  };

  const showHistory = async (source) => {
    setHistoryBusy(true); setHistory({ rowId: source.intake_row_id, events: [], pending: true });
    try {
      const response = await fetchTrainerDeferredAllocationHistory(source.intake_row_id);
      setHistory({ rowId: source.intake_row_id, ...response.data, pending: false });
    } catch (requestError) {
      setHistory(null);
      setError(errorMessage(requestError, "Could not load allocation history."));
    } finally { setHistoryBusy(false); }
  };

  const loadedRows = groups.reduce((count, group) => count + group.sources.length, 0);
  const unallocated = groups.reduce((count, group) => count + group.remaining_physical_quantity, 0);

  return <Stack spacing={2} sx={{ pb: 4 }}>
    <Box>
      <Typography variant="h5">Awaiting Allocation</Typography>
      <Typography variant="body2" color="text.secondary">
        Physically received Quick Intake cards awaiting singles/playset decisions. This action reserves existing stock against local listing targets. It does not receive new stock, publish listings or update eBay.
      </Typography>
    </Box>
    <Stack direction="row" gap={1} flexWrap="wrap" alignItems="center" useFlexGap>
      <Chip label={`${formatQuantity(unallocated)} unallocated cards loaded`} color="warning" />
      <Chip label={`${loadedRows} receipt rows / ${groups.length} exact prints`} />
      {hasMore && <Chip color="info" label="More receipt rows available" />}
      <Button variant="outlined" disabled={busy || loading} onClick={() => { setChosen(null); setPreview(null); setHistory(null); loadQueue(); }}>Refresh stock</Button>
      {loading && <CircularProgress size={20} aria-label="Loading awaiting allocation" />}
    </Stack>
    {pending && <Alert severity="warning" role="alert">
      <Typography fontWeight={700}>Allocation response pending verification — receipt #{pending.rowId}</Typography>
      Request ID: {pending.payload.request_id}. Singles: {pending.payload.single_allocation_quantity}, playsets: {pending.payload.playset_allocation_quantity}. Do not repeat this as a new allocation.
      <Stack direction="row" flexWrap="wrap" gap={1} sx={{ mt: 1 }}>
        <Button size="small" variant="contained" disabled={busy} onClick={() => sendAllocation(pending)}>Retry same allocation</Button>
        <Button size="small" variant="outlined" disabled={busy} onClick={verifyPending}>Check saved allocation history</Button>
      </Stack>
    </Alert>}
    {error && <Alert severity="error" role="alert">{error}</Alert>}
    {message && <Alert severity="success" role="status">{message}</Alert>}
    {!loading && !groups.length && <Alert severity="info">No unallocated, physically received Quick Intake rows found on the loaded page. Staged cards must be physically received first.</Alert>}

    <Stack spacing={1.5}>
      {groups.map((group) => <Card variant="outlined" key={`${group.catalog_card_id}::${group.card_variant}`}>
        <CardContent><Stack spacing={1}>
          <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" gap={1}>
            <Box>
              <Typography variant="subtitle1" fontWeight={700}>{group.name_and_number} — {group.card_variant}</Typography>
              <Typography variant="caption" color="text.secondary">Set: {group.set_name} · Catalogue #{group.catalog_card_id}</Typography>
            </Box>
            <Chip color="warning" label={`${formatQuantity(group.remaining_physical_quantity)} from loaded receipts`} />
          </Stack>
          <Stack direction="row" gap={1} useFlexGap flexWrap="wrap">
            <Chip size="small" label={`Physical on-hand: ${formatQuantity(group.quantity_on_hand)}`} />
            <Chip size="small" label={`Local singles target: ${formatQuantity(group.target_single_quantity)}`} />
            <Chip size="small" label={`Local playsets target: ${formatQuantity(group.target_playset_quantity)}`} />
            <Chip size="small" label={`Local available: ${formatQuantity(group.quantity_available_local)}`} />
          </Stack>
          <Typography variant="caption" color="text.secondary">
            Cached eBay (not live; no eBay lookup): singles {cachedQuantity(group.single_ebay_available_quantity, group.single_ebay_sync_status, group.single_last_ebay_sync_at)}; playsets {cachedQuantity(group.playset_ebay_available_quantity, group.playset_ebay_sync_status, group.playset_last_ebay_sync_at)}.
          </Typography>
          <TableContainer><Table size="small" aria-label={`Receipt sources for ${group.name_and_number}`}>
            <TableHead><TableRow>
              <TableCell>Receipt / batch</TableCell><TableCell align="right">Received</TableCell>
              <TableCell align="right">Previously allocated</TableCell><TableCell align="right">Remaining</TableCell><TableCell>Action</TableCell>
            </TableRow></TableHead>
            <TableBody>{group.sources.map((source) => <TableRow key={source.intake_row_id} selected={chosen?.source.intake_row_id === source.intake_row_id}>
              <TableCell>#{source.intake_row_id} · {source.batch_name || `Batch #${source.intake_batch_id}`}</TableCell>
              <TableCell align="right">{formatQuantity(source.received_quantity)}</TableCell>
              <TableCell align="right">{formatQuantity(source.legacy_singles + source.deferred_singles)} singles, {formatQuantity(source.legacy_playsets + source.deferred_playsets)} playsets</TableCell>
              <TableCell align="right">{source.blocked_reason ? "Review" : formatQuantity(source.remaining_quantity)}</TableCell>
              <TableCell><Stack direction="row" flexWrap="wrap" gap={0.5}>
                <Button size="small" variant="outlined" disabled={Boolean(source.blocked_reason) || !source.remaining_quantity || Boolean(pending) || busy}
                  onClick={() => selectReceipt(group, source)}>Allocate</Button>
                <Button size="small" disabled={historyBusy} onClick={() => showHistory(source)}>History</Button>
              </Stack>{source.blocked_reason && <Typography variant="caption" color="error">{source.blocked_reason}</Typography>}</TableCell>
            </TableRow>)}</TableBody>
          </Table></TableContainer>
        </Stack></CardContent>
      </Card>)}
    </Stack>
    {hasMore && <Button variant="outlined" disabled={loading || busy} onClick={() => loadQueue(afterId)}>Load more received cards</Button>}

    {chosen && !pending && <Card variant="outlined" sx={{ borderColor: "primary.main" }}>
      <CardContent><Stack spacing={2}>
        <Typography variant="h6">Allocate from receipt #{chosen.source.intake_row_id}</Typography>
        <Typography variant="body2">{chosen.group.name_and_number} · {chosen.group.set_name} · {chosen.group.card_variant}. Receipt-specific remaining: <strong>{chosen.source.remaining_quantity}</strong> physical cards. One playset reserves four cards.</Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}><TextField fullWidth size="small" type="number" label="Add singles (cards)" value={singles}
            inputProps={{ min: 0, max: 2147483647, step: 1 }} disabled={busy} onChange={(e) => changeQuantity("single", e.target.value)} /></Grid>
          <Grid item xs={12} sm={6}><TextField fullWidth size="small" type="number" label="Add playsets (sets of 4)" value={playsets}
            inputProps={{ min: 0, max: 2147483647, step: 1 }} disabled={busy} onChange={(e) => changeQuantity("playset", e.target.value)} /></Grid>
        </Grid>
        <Typography fontWeight={600}>Physical reservation requested: {formatQuantity(requested)} cards. Estimated receipt remainder: {formatQuantity(Math.max(0, Number(chosen.source.remaining_quantity) - requested))}.</Typography>
        {draftError && <Alert severity="info">{draftError}</Alert>}
        <Stack direction="row" gap={1}>
          <Button variant="contained" disabled={Boolean(draftError) || busy} onClick={reviewPreview}>Preview allocation</Button>
          <Button disabled={busy} onClick={() => { setChosen(null); setPreview(null); }}>Cancel</Button>
          <Button variant="outlined" onClick={() => onOpenInventory?.(chosen.group.name_and_number)}>View Inventory Board</Button>
        </Stack>
        {preview && <Box sx={{ border: 1, borderColor: "divider", borderRadius: 1, p: 2 }}>
          <Typography variant="h6">Server-verified allocation preview</Typography>
          <Typography variant="body2">On hand: {formatQuantity(preview.projection.quantity_on_hand)} · Current target: {formatQuantity(preview.projection.previous_single_target)} singles / {formatQuantity(preview.projection.previous_playset_target)} playsets.</Typography>
          <Typography variant="body2">Baseline: {formatQuantity(preview.projection.single_baseline_used)} singles ({preview.projection.single_baseline_source}), {formatQuantity(preview.projection.playset_baseline_used)} playsets ({preview.projection.playset_baseline_source}).</Typography>
          <Typography fontWeight={700}>After confirmation: {formatQuantity(preview.projection.target_single_after)} singles / {formatQuantity(preview.projection.target_playset_after)} playsets. {formatQuantity(preview.projection.quantity_available_after)} cards available locally; {formatQuantity(preview.projection.source_remaining_after)} left in this receipt.</Typography>
          <Alert severity="info" sx={{ my: 1 }}>Preview is read-only. Final apply rechecks receipt and stock under database locks. No eBay update or listing creation occurs.</Alert>
          <FormControlLabel control={<Checkbox checked={confirmed} onChange={(e) => setConfirmed(e.target.checked)} disabled={busy} />}
            label="I confirm these additional singles/playsets from this received stock" />
          <Box><Button color="primary" variant="contained" disabled={!confirmed || busy || Boolean(draftError)} onClick={confirmAllocation}>
            CONFIRM · APPLY LOCAL ALLOCATION
          </Button></Box>
        </Box>}
      </Stack></CardContent>
    </Card>}
    {history && <Card variant="outlined"><CardContent>
      <Stack direction="row" justifyContent="space-between"><Typography variant="h6">Receipt #{history.rowId} allocation history</Typography>
        <Button size="small" onClick={() => setHistory(null)}>Close</Button></Stack>
      {history.pending ? <CircularProgress size={18} /> : <>
        <Typography variant="body2">Originally allocated: {formatQuantity(history.legacy_single_allocation_quantity)} singles / {formatQuantity(history.legacy_playset_allocation_quantity)} playsets. Later allocations: {formatQuantity(history.deferred_single_allocated)} singles / {formatQuantity(history.deferred_playset_allocated)} playsets. Remaining: {history.remaining_quantity ?? "Review required"}.</Typography>
        {history.blocked_reason && <Alert severity="warning">{history.blocked_reason}</Alert>}
        <Divider sx={{ my: 1 }} />
        {history.events?.length ? history.events.map((event) => <Typography variant="body2" key={event.id}>
          Event #{event.id} · {event.created_at ? new Date(event.created_at).toLocaleString("en-AU") : ""} · +{event.single_allocation_quantity} singles, +{event.playset_allocation_quantity} playsets · {event.physical_units} cards · {event.request_id}
        </Typography>) : <Typography variant="body2">No deferred allocation events yet.</Typography>}
      </>}
    </CardContent></Card>}
  </Stack>;
}
